import React from 'react';
import { Sparkles, Sun, Users, BookOpen, Heart, Mountain, ArrowRight } from 'lucide-react';
import { buddhaLife } from '../data/mock';

const BuddhaLife = () => {
  const icons = [Sun, Mountain, Users, Sparkles, BookOpen, Heart];

  return (
    <div className="min-h-screen bg-[#ede1c5] text-stone-900">
      {/* HERO */}
      <section className="relative pt-32 pb-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/36481732/pexels-photo-36481732.jpeg)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#ede1c5]/85 via-[#ede1c5]/55 to-[#ede1c5]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-amber-400/12 blur-[160px]" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 text-amber-700/90 tracking-[0.3em] text-[11px] uppercase mb-6">
            <span className="h-px w-12 bg-amber-600/60" />
            <Sparkles size={14} />
            <span>Siddhārtha Gautama · 563 BCE</span>
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] text-stone-900 max-w-4xl">
            The Life of the<br /><span className="italic text-amber-800">Buddha.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-stone-700 leading-relaxed">
            From prince to the Awakened One — the extraordinary journey of Siddhārtha Gautama.
          </p>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 border-t border-amber-500/40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-stone-700 text-xl leading-relaxed">
            Born as Prince Siddhārtha Gautama around 563 BCE in Lumbini, Nepal, the Buddha embarked
            on one of history's most profound spiritual journeys. His teachings have guided
            millions toward peace, wisdom, and liberation for over 2,500 years.
          </p>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-24 bg-[#e3d4af] border-t border-amber-500/40">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              The Journey
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">
              Eight chapters of awakening
            </h2>
          </div>

          <div className="relative">
            {/* vertical line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-amber-300/30 to-transparent" />
            <div className="space-y-12">
              {buddhaLife.sections.map((section, index) => {
                const Icon = icons[index] || Sparkles;
                const isEven = index % 2 === 0;
                return (
                  <div
                    key={index}
                    className={`grid md:grid-cols-2 gap-8 items-center ${isEven ? '' : 'md:[&>*:first-child]:order-2'}`}
                  >
                    <div className={`relative p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80 hover:border-amber-500/40 transition-all ${isEven ? 'md:mr-10' : 'md:ml-10'}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-300 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20 flex-shrink-0">
                          <Icon className="text-black/80" size={18} />
                        </div>
                        <div>
                          <div className="text-[10px] tracking-[0.3em] uppercase text-amber-700/80 mb-1">
                            Chapter {String(index + 1).padStart(2, '0')}
                          </div>
                          <h3 className="font-serif-display text-2xl text-stone-900 leading-tight">
                            {section.title}
                          </h3>
                        </div>
                      </div>
                      <p className="text-stone-700 leading-relaxed mt-5">{section.content}</p>
                    </div>
                    <div className="hidden md:flex justify-center">
                      <div className="w-16 h-16 rounded-full bg-white border border-amber-500/40 text-amber-800 flex items-center justify-center font-serif-display text-2xl shadow-lg">
                        {index + 1}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SACRED MOMENTS */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              Visual Reverence
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">Sacred moments</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { url: 'https://images.unsplash.com/photo-1558980971-97f50d0fed00', t: 'The Awakened One' },
              { url: 'https://images.unsplash.com/photo-1529485726363-95c8d62f656f', t: 'Deep Meditation' },
              { url: 'https://images.pexels.com/photos/35898529/pexels-photo-35898529.jpeg', t: 'Eternal Peace' },
            ].map((img) => (
              <div key={img.t} className="group relative rounded-2xl overflow-hidden border border-amber-500/40 hover:border-amber-500/40 transition-all">
                <img src={img.url} alt={img.t} className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-amber-200 mb-1">Moment</div>
                  <div className="font-serif-display text-2xl text-white drop-shadow-md">{img.t}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE TEACHINGS */}
      <section className="py-24 bg-[#e3d4af] border-t border-amber-500/40">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              The Essence
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">
              Buddha's core teachings
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-10 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-[#fbf5e3]/95 to-[#f3e7c4]/90">
              <div className="text-[10px] tracking-[0.3em] uppercase text-amber-700/80 mb-3">Four Noble Truths</div>
              <h3 className="font-serif-display text-2xl text-stone-900 mb-6">The truth of things</h3>
              <ol className="space-y-3 text-stone-700">
                {['Dukkha — The truth of suffering', 'Samudaya — The cause of suffering', 'Nirodha — The end of suffering', 'Magga — The path to the end of suffering'].map((it, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="font-serif-display text-amber-800/80">{String(i + 1).padStart(2, '0')}</span>
                    <span className="leading-relaxed">{it}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="p-10 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-[#fbf5e3]/95 to-[#f3e7c4]/90">
              <div className="text-[10px] tracking-[0.3em] uppercase text-amber-700/80 mb-3">Noble Eightfold Path</div>
              <h3 className="font-serif-display text-2xl text-stone-900 mb-6">The way of practice</h3>
              <ol className="space-y-2.5 text-stone-700">
                {['Right Understanding', 'Right Intention', 'Right Speech', 'Right Action', 'Right Livelihood', 'Right Effort', 'Right Mindfulness', 'Right Concentration'].map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="font-serif-display text-amber-800/80 w-6">{String(i + 1).padStart(2, '0')}</span>
                    <span className="leading-relaxed">{it}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL WORDS */}
      <section
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/6689733/pexels-photo-6689733.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#ede1c5]/80 via-amber-50/70 to-[#fef3c7]/85" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-4">
            Last Words
          </div>
          <p className="font-serif-display italic text-3xl md:text-5xl text-stone-900 leading-tight">
            "All conditioned things are impermanent.<br />
            <span className="text-amber-800">Strive on with diligence."</span>
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 text-amber-800 text-sm tracking-[0.3em] uppercase">
            <span className="h-px w-10 bg-amber-600/60" /> The Buddha <span className="h-px w-10 bg-amber-600/60" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900 mb-5 leading-tight">
            Walk the <span className="italic text-amber-800">path.</span>
          </h2>
          <p className="text-stone-600 leading-relaxed mb-10">
            Explore the teachings and begin your own journey toward awakening.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/teachings" className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-600 text-black px-7 py-3.5 rounded-full text-sm tracking-wide">
              Explore Teachings <ArrowRight size={15} />
            </a>
            <a href="/programs" className="inline-flex items-center justify-center gap-2 border border-stone-300 hover:border-amber-300 text-stone-900 hover:text-amber-800 px-7 py-3.5 rounded-full text-sm tracking-wide">
              Join Our Programs
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BuddhaLife;
